import React, { useState, useRef, useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"

/**
 * node_modules
 */
import { getImage } from "gatsby-plugin-image"

/**
 * styles
 */
import * as styles from "@features/Home/Map/Map.module.scss"

/**
 * components
 */
import { ResponsiveImage } from "@components/ResponsiveImage"

/**
 * @function Map
 * @description HOME/Mapセクション
 */
export const Map = () => {
  /**
   * query
   */
  const data: Queries.HomeMapDataQuery = useStaticQuery(graphql`
    query HomeMapData {
      tokyoMobile: file(relativePath: { eq: "images/home/map-tokyo.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      tokyoDesktop: file(relativePath: { eq: "images/home/map-tokyo-desktop.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      osakaMobile: file(relativePath: { eq: "images/home/map-osaka.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      osakaDesktop: file(relativePath: { eq: "images/home/map-osaka-desktop.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      nagoyaMobile: file(relativePath: { eq: "images/home/map-nagoya.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      nagoyaDesktop: file(relativePath: { eq: "images/home/map-nagoya-desktop.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      allDataJson {
        nodes {
          home {
            map {
              heading
              notes {
                value
              }
              root {
                from
                id
                toList {
                  campsite
                  time
                }
              }
            }
          }
        }
      }
    }
  `)
  const jsonData = data.allDataJson.nodes
    .filter((item) => item.home !== null)
    .map((item) => item.home)
    .find((home) => home !== null)

  const initialArea = jsonData!.map!.root![0]!.id // 最初のアコーディオンのエリアを取得
  const [activeAccordion, setActiveAccordion] = useState(initialArea) // アクティブなアコーディオンを管理
  const [openIndex, setOpenIndex] = useState(0) // 開いているインデックスを管理
  const contentRefs = useRef<(HTMLDivElement | null)[]>([])

  const toggleAccordion = (index: number, area: string) => {
    // 同じインデックスなら反応なし、そうでなければ開く
    setOpenIndex((prevIndex) => (prevIndex === index ? prevIndex : index))
    setActiveAccordion(area) // クリックされたアコーディオンのエリアを設定
  }

  useEffect(() => {
    contentRefs.current.forEach((ref, index) => {
      if (ref) {
        ref.style.maxHeight = openIndex === index ? `${ref.scrollHeight}px` : "0px" // 状態に応じて高さを設定
      }
    })
  }, [openIndex])

  return (
    <section className={styles.map}>
      <h2 className={`${styles["inner__sp"]} ${styles["map_heading"]} ${styles["only__sp"]}`}>
        {jsonData!.map!.heading}
      </h2>
      <div className={styles["map_columnLeft"]}>
        {activeAccordion === "tokyo" && (
          <ResponsiveImage
            mobileImage={getImage(data.tokyoMobile!.childImageSharp!.gatsbyImageData)}
            desktopImage={getImage(data.tokyoDesktop!.childImageSharp!.gatsbyImageData)}
          />
        )}
        {activeAccordion === "osaka" && (
          <ResponsiveImage
            mobileImage={getImage(data.osakaMobile!.childImageSharp!.gatsbyImageData)}
            desktopImage={getImage(data.osakaDesktop!.childImageSharp!.gatsbyImageData)}
          />
        )}
        {activeAccordion === "nagoya" && (
          <ResponsiveImage
            mobileImage={getImage(data.nagoyaMobile!.childImageSharp!.gatsbyImageData)}
            desktopImage={getImage(data.nagoyaDesktop!.childImageSharp!.gatsbyImageData)}
          />
        )}
        <ul className={`${styles["map_notes"]} ${styles["only__pc"]}`}>
          {jsonData!.map!.notes!.map((notes, index) => (
            <li key={index}>{notes!.value}</li>
          ))}
        </ul>
      </div>
      <div className={styles["map_columnRight"]}>
        <h2 className={`${styles["map_heading"]} ${styles["only__pc"]}`}>
          {jsonData!.map!.heading}
        </h2>
        <div className={`${styles["inner__sp"]} ${styles["map_root"]}`}>
          {jsonData!.map!.root!.map((root, index) => (
            <div className={`${styles["map_rootItem"]}`} key={index}>
              <button
                className={`${styles["map_rootButton"]} ${openIndex === index ? styles.open : ""}`}
                data-area={root!.id}
                onClick={() => toggleAccordion(index, root!.id!)}
                aria-expanded={openIndex === index}
              >
                {root!.from}
              </button>
              <div
                className={`${styles["map_rootPanel"]}`}
                ref={(el) => (contentRefs.current[index] = el)}
              >
                {root!.toList!.map((to, index) => (
                  <p key={index}>
                    <span className={styles["map_rootCampsite"]}>{to!.campsite}</span>
                    <span>{to!.time}</span>
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={`${styles["only__sp"]}`}>
        <ul className={`${styles["inner__sp"]} ${styles["map_notes"]}`}>
          {jsonData!.map!.notes!.map((notes, index) => (
            <li key={index}>{notes!.value}</li>
          ))}
        </ul>
      </div>
    </section>
  )
}
